import { FormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { cloneDeep } from 'lodash-es';
import { debounceTime, merge, startWith } from 'rxjs';
import { updateVerbaleOnStore } from '@core/helpers/create-validator.helper';
import { getControl } from '@shared/helpers/form.helper';
import { nz, stringLengthInfoPathLike } from '@shared/helpers/utils.helper';
import { Utilities } from '@core/helpers/utilities';
import { withInitialValueDebounced } from '@shared/operators/with-initial-value-debounced.operator';
import { MyVerbale } from '../../../models/interfaces/revisione.interface';
import { Revisione53State } from '../../../store/revisione53.reducers';
import { formPaths } from '@shared/constants/form-paths.const';
import { getSchedaPrevalenzaGroup } from '../validators/helpers/natura-mutualistica-form-validators.helper';

export const setNatMutSchedaCalcoliListeners = (form: FormGroup, store: Store<Revisione53State>): void => {
    my_NumIscrAlboCoopMutP(form, store);
    my_TotPrevalSoci(form, store);
    my_TotPrevalTot(form, store);
    my_ValidaSchedaPrevalenza(form, store);
};

const my_NumIscrAlboCoopMutP = (form: FormGroup, store: Store<Revisione53State>): void => {
    const schedaPrevalenzaGroup = getSchedaPrevalenzaGroup(form);
    const my_PosizAlboEntiControl = getControl(form, `${formPaths.datiIdentificativi}.my_PosizAlboEnti`);
    const my_NumIscrAlboCoopMutPControl = getControl(schedaPrevalenzaGroup, 'my_NumIscrAlboCoopMutP');

    my_PosizAlboEntiControl.valueChanges.pipe(withInitialValueDebounced(my_PosizAlboEntiControl)).subscribe((my_PosizAlboEnti: string) => {
        my_NumIscrAlboCoopMutPControl.patchValue(my_PosizAlboEnti, { emitEvent: false });
        updateVerbaleOnStore(store, (myVerbale) => {
            const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
            myVerbaleClone.my_SezioneI__Rilevazione.my_NaturaMutualistica.my_SchedaPrevalenza.my_NumIscrAlboCoopMutP = my_PosizAlboEnti;
            return myVerbaleClone;
        });
    });
};

const my_TotPrevalSoci = (form: FormGroup, store: Store<Revisione53State>): void => {
    const schedaPrevalenzaGroup = getSchedaPrevalenzaGroup(form);
    const my_A1versoSociControl = getControl(schedaPrevalenzaGroup, 'my_A1versoSoci');
    const my_B9versoSociControl = getControl(schedaPrevalenzaGroup, 'my_B9versoSoci');
    const my_B6confSociControl = getControl(schedaPrevalenzaGroup, 'my_B6confSoci');
    const my_B7ricevSociControl = getControl(schedaPrevalenzaGroup, 'my_B7ricevSoci');
    const my_TotPrevalSociControl = getControl(schedaPrevalenzaGroup, 'my_TotPrevalSoci');

    merge(
        my_A1versoSociControl.valueChanges.pipe(startWith(my_A1versoSociControl.value)),
        my_B9versoSociControl.valueChanges.pipe(startWith(my_B9versoSociControl.value)),
        my_B6confSociControl.valueChanges.pipe(startWith(my_B6confSociControl.value)),
        my_B7ricevSociControl.valueChanges.pipe(startWith(my_B7ricevSociControl.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const my_TotPrevalSoci = nz(my_A1versoSociControl.value) + nz(my_B9versoSociControl.value) + nz(my_B6confSociControl.value) + nz(my_B7ricevSociControl.value);
            my_TotPrevalSociControl.patchValue(my_TotPrevalSoci, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_NaturaMutualistica.my_SchedaPrevalenza.my_TotPrevalSoci = my_TotPrevalSoci.toString(10);
                return myVerbaleClone;
            });
        });
};

const my_TotPrevalTot = (form: FormGroup, store: Store<Revisione53State>): void => {
    const schedaPrevalenzaGroup = getSchedaPrevalenzaGroup(form);
    const my_TotaleA1Control = getControl(schedaPrevalenzaGroup, 'my_TotaleA1');
    const my_TotaleB9Control = getControl(schedaPrevalenzaGroup, 'my_TotaleB9');
    const my_TotaleB6Control = getControl(schedaPrevalenzaGroup, 'my_TotaleB6');
    const my_TotaleB7Control = getControl(schedaPrevalenzaGroup, 'my_TotaleB7');
    const my_TotPrevalTotControl = getControl(schedaPrevalenzaGroup, 'my_TotPrevalTot');

    merge(
        my_TotaleA1Control.valueChanges.pipe(startWith(my_TotaleA1Control.value)),
        my_TotaleB9Control.valueChanges.pipe(startWith(my_TotaleB9Control.value)),
        my_TotaleB6Control.valueChanges.pipe(startWith(my_TotaleB6Control.value)),
        my_TotaleB7Control.valueChanges.pipe(startWith(my_TotaleB7Control.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const my_TotPrevalTot = nz(my_TotaleA1Control.value) + nz(my_TotaleB9Control.value) + nz(my_TotaleB6Control.value) + nz(my_TotaleB7Control.value);
            my_TotPrevalTotControl.patchValue(my_TotPrevalTot, { emitEvent: false });
            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                myVerbaleClone.my_SezioneI__Rilevazione.my_NaturaMutualistica.my_SchedaPrevalenza.my_TotPrevalTot = my_TotPrevalTot.toString(10);
                return myVerbaleClone;
            });
        });
};

const my_ValidaSchedaPrevalenza = (form: FormGroup, store: Store<Revisione53State>): void => {
    const schedaPrevalenzaGroup = getSchedaPrevalenzaGroup(form);
    const my_A1versoSociControl = getControl(schedaPrevalenzaGroup, 'my_A1versoSoci');
    const my_TotaleA1Control = getControl(schedaPrevalenzaGroup, 'my_TotaleA1');
    const my_B9versoSociControl = getControl(schedaPrevalenzaGroup, 'my_B9versoSoci');
    const my_TotaleB9Control = getControl(schedaPrevalenzaGroup, 'my_TotaleB9');
    const my_B6confSociControl = getControl(schedaPrevalenzaGroup, 'my_B6confSoci');
    const my_TotaleB6Control = getControl(schedaPrevalenzaGroup, 'my_TotaleB6');
    const my_B7ricevSociControl = getControl(schedaPrevalenzaGroup, 'my_B7ricevSoci');
    const my_TotaleB7Control = getControl(schedaPrevalenzaGroup, 'my_TotaleB7');
    const my_QtaConfSociControl = getControl(schedaPrevalenzaGroup, 'my_QtaConfSoci');
    const my_TotQtaConfSociControl = getControl(schedaPrevalenzaGroup, 'my_TotQtaConfSoci');
    const my_validaSchedaControl = getControl(schedaPrevalenzaGroup, 'my_validaScheda');
    const my_validaScheda1Control = getControl(schedaPrevalenzaGroup, 'my_validaScheda1');

    merge(
        my_A1versoSociControl.valueChanges.pipe(startWith(my_A1versoSociControl?.value)),
        my_TotaleA1Control.valueChanges.pipe(startWith(my_TotaleA1Control?.value)),
        my_B9versoSociControl.valueChanges.pipe(startWith(my_B9versoSociControl?.value)),
        my_TotaleB9Control.valueChanges.pipe(startWith(my_TotaleB9Control?.value)),
        my_B6confSociControl.valueChanges.pipe(startWith(my_B6confSociControl?.value)),
        my_TotaleB6Control.valueChanges.pipe(startWith(my_TotaleB6Control?.value)),
        my_B7ricevSociControl.valueChanges.pipe(startWith(my_B7ricevSociControl?.value)),
        my_TotaleB7Control.valueChanges.pipe(startWith(my_TotaleB7Control?.value)),
        my_QtaConfSociControl.valueChanges.pipe(startWith(my_QtaConfSociControl?.value)),
        my_TotQtaConfSociControl.valueChanges.pipe(startWith(my_TotQtaConfSociControl?.value))
    )
        .pipe(debounceTime(Utilities.LISTENERS_DEBOUNCE_TIME))
        .subscribe(() => {
            const a1VersoSoci = stringLengthInfoPathLike(my_A1versoSociControl.value);
            const totaleA1 = stringLengthInfoPathLike(my_TotaleA1Control.value);
            const b9VersoSoci = stringLengthInfoPathLike(my_B9versoSociControl.value);
            const totaleB9 = stringLengthInfoPathLike(my_TotaleB9Control.value);
            const b6ConfSoci = stringLengthInfoPathLike(my_B6confSociControl.value);
            const totaleB6 = stringLengthInfoPathLike(my_TotaleB6Control.value);
            const b7RicevSoci = stringLengthInfoPathLike(my_B7ricevSociControl.value);
            const totaleB7 = stringLengthInfoPathLike(my_TotaleB7Control.value);
            const qtaConfSoci = stringLengthInfoPathLike(my_QtaConfSociControl.value);
            const totQtaConfSoci = stringLengthInfoPathLike(my_TotQtaConfSociControl.value);

            const validaScheda =
                a1VersoSoci * totaleA1 + b9VersoSoci * totaleB9 + b6ConfSoci * totaleB6 * b7RicevSoci * totaleB7 + qtaConfSoci * totQtaConfSoci * b7RicevSoci * totaleB7;
            const validaScheda1 =
                a1VersoSoci * totaleA1 * b9VersoSoci * totaleB9 +
                a1VersoSoci * totaleA1 * b6ConfSoci * totaleB6 * b7RicevSoci * totaleB7 +
                b6ConfSoci * totaleB6 * b7RicevSoci * totaleB7 * b9VersoSoci * totaleB9;

            my_validaSchedaControl.patchValue(validaScheda, { emitEvent: false });
            my_validaScheda1Control.patchValue(validaScheda1, { emitEvent: false });

            updateVerbaleOnStore(store, (myVerbale) => {
                const myVerbaleClone: MyVerbale = cloneDeep(myVerbale);
                const sez = myVerbaleClone.my_SezioneI__Rilevazione.my_NaturaMutualistica.my_SchedaPrevalenza;
                sez.my_validaScheda = validaScheda.toString(10);
                sez.my_validaScheda1 = validaScheda1.toString(10);
                return myVerbaleClone;
            });
        });
};
